import React, { useState, useEffect } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';

interface MigrateMountDialogProps {
  id: string; // storeId
  mountPoints?: any[]; // List of existing mount points for source selection
  // Pre-fill source from specific mount point (optional)
  defaultSourceHostname?: string;
  defaultSourceType?: 'hostname' | 'alias' | 'customDomain';
  children?: React.ReactNode; // Custom trigger
}

/**
 * Dialog to migrate all routes from one mount point to another
 * Use cases: rename alias, deploy to production (alias → custom domain)
 */
export function MigrateMountDialog({
  id,
  mountPoints = [],
  defaultSourceHostname,
  defaultSourceType,
  children,
}: MigrateMountDialogProps) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);

  // Source configuration - initialize from defaults if provided
  const [sourceType, setSourceType] = useState<'hostname' | 'alias' | 'customDomain'>(
    defaultSourceType || 'alias',
  );
  const [sourceValue, setSourceValue] = useState(defaultSourceHostname || '');
  const [sourceFromList, setSourceFromList] = useState(''); // For selecting from existing mount points

  // Reset source when dialog opens if defaultSourceHostname is provided
  useEffect(() => {
    if (open && defaultSourceHostname) {
      setSourceValue(defaultSourceHostname);
      setSourceFromList('');
    }
  }, [open, defaultSourceHostname]);

  // Target configuration
  const [targetType, setTargetType] = useState<'hostname' | 'alias' | 'customDomain'>('alias');
  const [targetValue, setTargetValue] = useState('');

  // Get verified domains for custom domain option
  const { data: domainsData } = trpc.plugins.listVerifiedDomains.useQuery(undefined, {
    enabled: open && (sourceType === 'customDomain' || targetType === 'customDomain'),
  });

  const verifiedDomains = domainsData?.domains || [];

  const migrateMutation = trpc.plugins.migrateMountPoint.useMutation({
    onSuccess: (result) => {
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: result.message || `Migrated ${result.routesMigrated} route(s)`,
      });
      handleClose();
    },
    onError: (error) => {
      toast({
        title: 'Migration Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleClose = () => {
    setOpen(false);
    setSourceType('alias');
    setSourceValue('');
    setSourceFromList('');
    setTargetType('alias');
    setTargetValue('');
  };

  const handleSubmit = () => {
    const finalSourceValue = sourceFromList || sourceValue;

    if (!finalSourceValue.trim() || !targetValue.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Both source and target must be specified',
        variant: 'destructive',
      });
      return;
    }

    // Convert targetType back to 'custom-domain' if needed
    const finalTargetType = targetType === 'customDomain' ? 'customDomain' : targetType;
    const finalSourceType = sourceType === 'customDomain' ? 'customDomain' : sourceType;

    if (finalSourceValue === targetValue && finalSourceType === finalTargetType) {
      toast({
        title: 'Validation Error',
        description: 'Source and target cannot be the same',
        variant: 'destructive',
      });
      return;
    }

    migrateMutation.mutate({
      storeId: id,
      from: {
        [finalSourceType]: finalSourceValue.trim(),
      },
      to: {
        [finalTargetType]: targetValue.trim(),
      },
    });
  };

  // Extract unique aliases from mount points with full hostname info
  const existingAliases = mountPoints
    .filter((mp: any) => mp.routeType === 'alias')
    .map((mp: any) => ({
      hostname: mp.hostname, // Full URL: loic2--store_123.cdn.localhost:3000
      shortName: mp.hostname.split('--')[0] || mp.hostname, // Extract short name: loic2
    }))
    .filter((v, i, a) => a.findIndex((x) => x.hostname === v.hostname) === i);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" className="gap-2">
            <ArrowRight className="h-4 w-4" />
            Migrate Mount Point
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Migrate Mount Point</DialogTitle>
          <DialogDescription>
            Move all routes from one mount point to another. Useful for renaming aliases or deploying to
            production (alias → custom domain).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* SOURCE Section */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Source (Migrate FROM)</Label>
              <p className="text-muted-foreground text-xs">Select the mount point to migrate routes from</p>
            </div>

            <RadioGroup
              value={sourceType}
              onValueChange={(v: any) => !defaultSourceHostname && setSourceType(v)}
              disabled={!!defaultSourceHostname}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="alias" id="source-alias" disabled={!!defaultSourceHostname} />
                <Label htmlFor="source-alias" className="font-normal">
                  Alias
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hostname" id="source-hostname" disabled={!!defaultSourceHostname} />
                <Label htmlFor="source-hostname" className="font-normal">
                  Hostname
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="customDomain"
                  id="source-customDomain"
                  disabled={!!defaultSourceHostname}
                />
                <Label htmlFor="source-customDomain" className="font-normal">
                  Custom Domain
                </Label>
              </div>
            </RadioGroup>

            <div className="space-y-2">
              {existingAliases.length > 0 && sourceType === 'alias' && !defaultSourceHostname && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs">Quick Select (Existing Aliases)</Label>
                  <Select
                    value={sourceFromList}
                    onValueChange={(value) => {
                      setSourceFromList(value);
                      setSourceValue(value); // Also set the input value with full hostname
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an existing alias..." />
                    </SelectTrigger>
                    <SelectContent>
                      {existingAliases.map((alias) => (
                        <SelectItem key={alias.hostname} value={alias.hostname}>
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{alias.shortName}</span>
                            <span className="text-muted-foreground font-mono text-xs">{alias.hostname}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">Or type the full hostname manually below:</p>
                </div>
              )}

              {sourceType === 'customDomain' && verifiedDomains.length > 0 && !defaultSourceHostname ? (
                <Select value={sourceValue} onValueChange={setSourceValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a verified domain..." />
                  </SelectTrigger>
                  <SelectContent>
                    {verifiedDomains.map((domain: any) => (
                      <SelectItem key={domain.id} value={domain.domain}>
                        {domain.domain}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder={
                    sourceType === 'alias'
                      ? 'my-alias-name'
                      : sourceType === 'hostname'
                        ? 'store.example.com'
                        : 'checkout.mysite.com'
                  }
                  value={sourceValue}
                  onChange={(e) => setSourceValue(e.target.value)}
                  disabled={(!!sourceFromList && sourceType === 'alias') || !!defaultSourceHostname}
                />
              )}
              {defaultSourceHostname && (
                <p className="text-muted-foreground text-xs">
                  Source pre-filled from mount point:{' '}
                  <code className="bg-muted rounded px-1 py-0.5">{defaultSourceHostname}</code>
                </p>
              )}
            </div>
          </div>

          {/* ARROW Divider */}
          <div className="flex items-center justify-center">
            <div className="border-muted-foreground/20 flex h-10 w-10 items-center justify-center rounded-full border-2">
              <ArrowRight className="text-muted-foreground h-5 w-5" />
            </div>
          </div>

          {/* TARGET Section */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Target (Migrate TO)</Label>
              <p className="text-muted-foreground text-xs">Select the new mount point destination</p>
            </div>

            <RadioGroup value={targetType} onValueChange={(v: any) => setTargetType(v)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="alias" id="target-alias" />
                <Label htmlFor="target-alias" className="font-normal">
                  Alias
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hostname" id="target-hostname" />
                <Label htmlFor="target-hostname" className="font-normal">
                  Hostname
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="customDomain" id="target-customDomain" />
                <Label htmlFor="target-customDomain" className="font-normal">
                  Custom Domain (Production)
                </Label>
              </div>
            </RadioGroup>

            <div className="space-y-2">
              {targetType === 'customDomain' && verifiedDomains.length > 0 ? (
                <>
                  <Select value={targetValue} onValueChange={setTargetValue}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a verified domain..." />
                    </SelectTrigger>
                    <SelectContent>
                      {verifiedDomains.map((domain: any) => (
                        <SelectItem key={domain.id} value={domain.domain}>
                          {domain.domain}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {verifiedDomains.length === 0 && (
                    <p className="text-muted-foreground text-xs">
                      No verified custom domains found. Add one in Settings.
                    </p>
                  )}
                </>
              ) : (
                <Input
                  placeholder={
                    targetType === 'alias'
                      ? 'new-alias-name'
                      : targetType === 'hostname'
                        ? 'newstore.example.com'
                        : 'checkout.mysite.com'
                  }
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={migrateMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={migrateMutation.isPending}>
            {migrateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Migrating...
              </>
            ) : (
              'Migrate Routes'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
